'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useExtracted } from 'next-intl'
import { X } from '@/components/ui/icons'

interface DocImageProps {
  src: string
  alt: string
  /** Inline screenshot that ships with a baked-in frame: fill the box (object-cover),
   * drop the container border, and scale up slightly so overflow-hidden crops the
   * hairline border off the source. Gallery screenshots leave this off (object-contain). */
  zoom?: boolean
  /** Preserve the source aspect ratio for tightly cropped instructional images. */
  natural?: boolean
}

/** A docs screenshot that opens to a full-screen lightbox on click (Esc closes). */
export function DocImage({ src, alt, zoom, natural }: DocImageProps) {
  const t = useExtracted()
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, close])

  useEffect(() => {
    // A cached image can finish loading before onLoad attaches; sync loaded state once on mount.
    if (imgRef.current?.complete) {
      setLoaded(true)
    }
  }, [])

  return (
    <>
      <div
        data-doc-image-fit={natural ? 'natural' : 'frame'}
        className={`${natural ? 'w-fit max-w-full' : 'aspect-video'} cursor-zoom-in overflow-hidden rounded-xl bg-card/20 transition-opacity hover:opacity-90 ${zoom || natural ? '' : 'border border-border/40'}`}
        onClick={() => setOpen(true)}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          className={`${natural ? 'block h-auto max-w-full' : 'h-full w-full'} transition-opacity duration-300 ${natural ? '' : zoom ? 'scale-[1.03] object-cover' : 'object-contain'} ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={close}
        >
          <button
            onClick={close}
            className="absolute end-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label={t('Close')}
          >
            <X className="h-4 w-4" />
          </button>

          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt={alt} className="block max-h-[90vh] max-w-[90vw] object-contain" />
          </div>
        </div>
      )}
    </>
  )
}
